/* Napurel cart drawer (Z12), template-scoped.
   - Implements the cart contract Dawn's product-form.js expects (getSectionsToRender / renderContents /
     setActiveElement) and assigns itself to every <product-form> on this page, so add to cart goes through
     the theme's own form + /cart/add flow and returns this drawer's HTML via the Section Rendering API.
   - Line quantity / remove / note use /cart/change.js and /cart/update.js with the same section re-render.
   - Max sets per order is checked here and before the product form submits (pp-main-product.js). */
(function () {
  if (customElements.get('pp-cart-drawer')) return;

  class PpCartDrawer extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.max = parseInt(this.dataset.max, 10) || 0;
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-pp-cart-note]')) this.saveNote(event.target.value);
        if (event.target.matches('[data-pp-opt]')) this.swapVariant(event.target);
      });
      this.onKey = (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
      };
      document.addEventListener('keydown', this.onKey);

      // Header cart icon opens this drawer instead of navigating to /cart.
      this.onIconClick = (event) => {
        const icon = event.target.closest('#cart-icon-bubble');
        if (!icon) return;
        event.preventDefault();
        this.open(icon);
      };
      document.addEventListener('click', this.onIconClick);

      const attach = () => {
        document.querySelectorAll('product-form').forEach((form) => {
          form.cart = this;
          const button = form.querySelector('[type="submit"]');
          if (button) button.setAttribute('aria-haspopup', 'dialog');
        });
      };
      if (window.customElements) customElements.whenDefined('product-form').then(attach);
      document.addEventListener('DOMContentLoaded', attach);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('click', this.onIconClick);
    }

    get inner() {
      return this.querySelector(`#pp-cart-inner-${this.sectionId}`);
    }

    productQty() {
      return parseInt(this.inner?.dataset.productQty || '0', 10);
    }

    canAdd(qty) {
      return !this.max || this.productQty() + qty <= this.max;
    }

    /* ---- product-form.js contract ---- */
    getSectionsToRender() {
      return [{ id: this.sectionId }, { id: 'cart-icon-bubble' }];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    renderContents(response, { added = true } = {}) {
      const sections = response.sections || {};
      this.renderSections(sections);
      if (added) this.setTopLine(this.dataset.addedLine);
      this.open();
    }

    renderSections(sections) {
      const html = sections[this.sectionId];
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.getElementById(`pp-cart-inner-${this.sectionId}`);
        if (source && this.inner) this.inner.replaceWith(source);
      }
      const bubble = sections['cart-icon-bubble'];
      const target = document.getElementById('cart-icon-bubble');
      if (bubble && target) {
        const doc = new DOMParser().parseFromString(bubble, 'text/html');
        const source = doc.querySelector('.shopify-section');
        if (source) target.innerHTML = source.innerHTML;
      }
    }

    /* ---- open / close ---- */
    open(opener) {
      if (opener) this.activeElement = opener;
      this.classList.add('is-open');
      document.body.classList.add('pp-cart-open');
      document.dispatchEvent(new CustomEvent('pp:cart-toggle', { detail: { open: true } }));
      const panel = this.querySelector('.pp-cart__panel');
      setTimeout(() => {
        if (typeof trapFocus === 'function') trapFocus(panel, this.querySelector('.pp-cart__close'));
        else panel.focus();
      }, 50);
    }

    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('pp-cart-open');
      this.showError('');
      this.setTopLine('');
      document.dispatchEvent(new CustomEvent('pp:cart-toggle', { detail: { open: false } }));
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
      else if (this.activeElement) this.activeElement.focus();
    }

    setTopLine(text) {
      const el = this.querySelector('[data-pp-cart-top-text]');
      if (!el) return;
      if (!el.dataset.default) el.dataset.default = el.textContent;
      el.textContent = text || el.dataset.default;
    }

    showError(message) {
      const el = this.querySelector('[data-pp-cart-error]');
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
    }

    /* ---- line changes ---- */
    onClick(event) {
      if (event.target.closest('[data-pp-cart-close]')) {
        event.preventDefault();
        this.close();
        return;
      }
      const item = event.target.closest('.pp-cart__item');
      if (!item) return;
      const line = parseInt(item.dataset.line, 10);
      const current = parseInt(item.dataset.qty, 10);
      const step = event.target.closest('[data-pp-qty]');
      if (step) {
        const delta = parseInt(step.dataset.ppQty, 10);
        if (delta > 0 && !this.canAdd(1) && item.dataset.productId) {
          this.showError(this.dataset.maxError);
          return;
        }
        this.change(line, Math.max(0, current + delta));
      } else if (event.target.closest('[data-pp-remove]')) {
        this.change(line, 0);
      }
    }

    request(url, body) {
      this.classList.add('is-busy');
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...body,
          sections: this.getSectionsToRender().map((s) => s.id),
          sections_url: window.location.pathname,
        }),
      })
        .then((r) => r.json())
        .finally(() => this.classList.remove('is-busy'));
    }

    change(line, quantity) {
      this.showError('');
      this.request(this.dataset.cartChangeUrl || '/cart/change.js', { line, quantity })
        .then((state) => {
          if (state.errors || state.status) {
            this.showError(state.description || state.message || state.errors);
            return;
          }
          this.renderSections(state.sections || {});
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pp-cart-drawer', cartData: state });
          }
        })
        .catch(() => this.showError(window.cartStrings?.error || 'Something went wrong. Try again.'));
    }

    /* Customer changes size/color on a line: replace the line with the matching variant, same quantity */
    swapVariant(select) {
      const item = select.closest('.pp-cart__item');
      const box = select.closest('[data-pp-line-opts]');
      if (!item || !box) return;
      let variants = [];
      try {
        variants = JSON.parse(box.querySelector('[data-pp-variants]').textContent);
      } catch (e) {}
      const chosen = Array.from(box.querySelectorAll('[data-pp-opt]')).map((s) => s.value);
      const match = variants.find((v) => v.o.every((val, i) => val === chosen[i]));
      const line = parseInt(item.dataset.line, 10);
      const qty = parseInt(item.dataset.qty, 10) || 1;
      const restore = () => {
        const current = variants.find((v) => String(v.id) === item.dataset.variantId);
        if (current) box.querySelectorAll('[data-pp-opt]').forEach((s, i) => (s.value = current.o[i]));
      };
      if (!match || !match.a) {
        this.showError(this.dataset.variantUnavailable || 'This option is sold out.');
        restore();
        return;
      }
      if (String(match.id) === item.dataset.variantId) return;
      this.showError('');
      this.request(this.dataset.cartChangeUrl || '/cart/change.js', { line, quantity: 0 })
        .then(() => this.request(window.routes?.cart_add_url ? `${window.routes.cart_add_url}.js` : '/cart/add.js', { items: [{ id: match.id, quantity: qty }] }))
        .then((state) => {
          if (state.status || state.errors) {
            this.showError(state.description || state.message || this.dataset.variantUnavailable);
          }
          // /cart/add.js returns the added items; refresh the drawer through the Section Rendering API
          return fetch(`${window.location.pathname}?sections=${this.getSectionsToRender().map((s) => s.id).join(',')}`)
            .then((r) => r.json())
            .then((sections) => this.renderSections(sections));
        })
        .then(() => {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pp-cart-drawer' });
          }
        })
        .catch(() => this.showError(window.cartStrings?.error || 'Something went wrong. Try again.'));
    }

    saveNote(note) {
      fetch(this.dataset.cartUpdateUrl || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      });
    }
  }

  customElements.define('pp-cart-drawer', PpCartDrawer);
})();
